import { ROLE_LABELS, type WorkspaceDto } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth-layout";
import { ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { queries } from "@/lib/queries";

export const Route = createFileRoute("/convite/$token")({
  component: ConvitePage,
});

function ConvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = authClient.useSession();
  const preview = useQuery(queries.invitePreview(token));
  const redirecionar = `/convite/${token}`;

  const aceitar = useMutation({
    mutationFn: () => api<WorkspaceDto>(`/api/invites/${token}/aceitar`, { method: "POST" }),
    onSuccess: async (ws) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success(`Você entrou em ${ws.name}.`);
      await navigate({ to: "/app/$workspaceId", params: { workspaceId: ws.id } });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <AuthLayout title="Convite para um workspace">
      {preview.isPending ? (
        <LoadingState rows={2} />
      ) : preview.isError ? (
        <ErrorState error={preview.error} title="Convite não encontrado" />
      ) : (
        <div className="space-y-4">
          <p className="text-sm">
            {preview.data.invitedByName ? <strong>{preview.data.invitedByName}</strong> : "Alguém"}{" "}
            convidou <strong>{preview.data.email}</strong> para entrar em{" "}
            <strong>{preview.data.workspaceName}</strong> como{" "}
            <strong>{ROLE_LABELS[preview.data.role]}</strong>.
          </p>

          {preview.data.status !== "pendente" ? (
            <Alert variant="destructive">
              <AlertDescription>
                {preview.data.status === "aceito"
                  ? "Este convite já foi usado."
                  : preview.data.status === "expirado"
                    ? "Este convite expirou. Peça um novo para quem convidou você."
                    : "Este convite foi cancelado."}
              </AlertDescription>
            </Alert>
          ) : session.isPending ? (
            <LoadingState rows={1} />
          ) : session.data ? (
            <div className="space-y-3">
              {session.data.user.email.toLowerCase() !== preview.data.email.toLowerCase() ? (
                <Alert>
                  <AlertDescription>
                    Você está logado como {session.data.user.email}, mas o convite é para{" "}
                    {preview.data.email}. Saia e entre com a conta certa.
                  </AlertDescription>
                </Alert>
              ) : null}
              <Button
                className="w-full"
                loading={aceitar.isPending}
                onClick={() => aceitar.mutate()}
              >
                Aceitar convite
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={async () => {
                  await authClient.signOut();
                  await queryClient.invalidateQueries();
                }}
              >
                Sair e usar outra conta
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Entre ou crie uma conta com o e-mail {preview.data.email} para aceitar.
              </p>
              <Button asChild className="w-full">
                <Link to="/entrar" search={{ redirecionar }}>
                  Entrar
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/cadastro" search={{ redirecionar }}>
                  Criar conta
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </AuthLayout>
  );
}
